import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  MapPin, Star, Clock, Users, ChevronLeft, ChevronRight, Sparkles,
  ShieldCheck, Award, Check, X, Calendar, Plane, Building, Utensils,
  Car, Wifi, Coffee, Heart, Share2, MessageCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sample reviews for social proof
const SAMPLE_REVIEWS = [
  {
    id: 1,
    name: 'María García',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
    rating: 5,
    date: 'Hace 2 semanas',
    comment: '¡Increíble experiencia! El hotel superó todas mis expectativas. Definitivamente volveré a reservar con Bombazoo.',
    verified: true
  },
  {
    id: 2,
    name: 'Carlos Mendoza',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
    rating: 5,
    date: 'Hace 1 mes',
    comment: 'El ahorro fue real. Comparé precios y efectivamente pagué casi la mitad de lo que costaba en Booking. Excelente servicio.',
    verified: true
  },
  {
    id: 3,
    name: 'Ana López',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop',
    rating: 4,
    date: 'Hace 3 semanas',
    comment: 'Muy buena relación calidad-precio. La presentación del timeshare fue rápida y sin presión. Recomendado.',
    verified: true
  }
];

export default function PackageDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showReservationForm, setShowReservationForm] = useState(false);
  const [reservationData, setReservationData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    address: '',
    phone1: '',
    phone2: '',
    travel_date: '',
    num_persons: 2,
    agent_name: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    travelers: 2
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPackage();
  }, [id]);

  const fetchPackage = async () => {
    try {
      const response = await fetch(`${API}/certificates/${id}`);
      if (response.ok) {
        const data = await response.json();
        setPkg(data);
      } else {
        toast.error('Paquete no encontrado');
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching package:', error);
      toast.error('Error al cargar el paquete');
    } finally {
      setLoading(false);
    }
  };

  // Handle reservation submission
  const handleReservationSubmit = async () => {
    if (!reservationData.first_name || !reservationData.last_name || !reservationData.email || !reservationData.phone1) {
      toast.error('Por favor completa los campos requeridos');
      return;
    }

    setSubmitting(true);
    
    try {
      const response = await fetch(`${API}/certificates/reservations/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          certificate_id: pkg.certificate_id,
          certificate_title: pkg.title,
          ...reservationData
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('¡Reservación registrada exitosamente!');
        
        // If there's a payment link, redirect to it
        if (pkg.stripe_link) {
          toast.info('Redirigiendo al pago...', { duration: 2000 });
          setTimeout(() => {
            window.open(pkg.stripe_link, '_blank');
          }, 1500);
        } else {
          toast.info('Un agente se pondrá en contacto contigo para completar el pago.');
        }
        
        setShowReservationForm(false);
        setReservationData({
          first_name: '',
          last_name: '',
          email: '',
          address: '',
          phone1: '',
          phone2: '',
          travel_date: '',
          num_persons: 2,
          agent_name: ''
        });
      } else {
        toast.error(data.detail || 'Error al crear la reservación');
      }
    } catch (error) {
      console.error('Reservation error:', error);
      toast.error('Error de conexión');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckout = async () => {
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setSubmitting(true);
    
    // If package has a stripe_link, redirect there
    if (pkg.stripe_link) {
      window.location.href = pkg.stripe_link;
      return;
    }

    // Otherwise show a message that payment will be configured
    toast.success('¡Solicitud enviada! Nos pondremos en contacto contigo pronto.');
    setShowCheckout(false);
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!pkg) {
    return null;
  }

  const images = pkg.images?.length > 0 ? pkg.images : [
    'https://images.unsplash.com/photo-1693934304978-22274e6a3276?w=1200&q=85'
  ];

  const bookingPrice = pkg.comparison_booking || pkg.retail_price * 1.8;
  const savings = bookingPrice - pkg.retail_price;
  const savingsPercent = Math.round((savings / bookingPrice) * 100);
  const rating = pkg.rating || 4.8;
  const reviewsCount = pkg.reviews_count || 127;

  const inclusions = pkg.inclusions?.length > 0 ? pkg.inclusions : [
    'Alojamiento en hotel de lujo',
    'Desayuno buffet diario',
    'Traslados aeropuerto-hotel',
    'Acceso a piscina y gimnasio',
    'WiFi de alta velocidad'
  ];

  const exclusions = pkg.exclusions?.length > 0 ? pkg.exclusions : [
    'Vuelos (no incluidos)',
    'Propinas',
    'Gastos personales'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
            >
              <ChevronLeft className="w-5 h-5" />
              Volver
            </Button>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="text-slate-500">
                <Share2 className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="icon" className="text-slate-500">
                <Heart className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-16">
        {/* Image Gallery */}
        <div className="relative h-[50vh] md:h-[60vh] bg-slate-100">
          <img
            src={images[currentImageIndex]}
            alt={pkg.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          
          {/* Image Navigation */}
          {images.length > 1 && (
            <>
              <button
                onClick={() => setCurrentImageIndex(prev => prev === 0 ? images.length - 1 : prev - 1)}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-slate-700" />
              </button>
              <button
                onClick={() => setCurrentImageIndex(prev => (prev + 1) % images.length)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
              >
                <ChevronRight className="w-6 h-6 text-slate-700" />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      idx === currentImageIndex ? 'bg-white w-8' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </>
          )}

          {/* Savings Badge */}
          <div className="absolute top-4 left-4 bg-rose-500 text-white px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2 shadow-lg">
            <Sparkles className="w-4 h-4" />
            Ahorras {savingsPercent}%
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Header */}
              <div>
                <div className="flex items-center gap-2 text-teal-600 font-medium mb-2">
                  <MapPin className="w-5 h-5" />
                  {pkg.destination}
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                  {pkg.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${i < Math.floor(rating) ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`}
                      />
                    ))}
                    <span className="ml-2 font-semibold text-slate-900">{rating}</span>
                    <span className="text-slate-500">({reviewsCount} opiniones)</span>
                  </div>

                  {/* TripAdvisor Rating */}
                  {pkg.tripadvisor_rating && (
                    <a 
                      href={pkg.tripadvisor_link || '#'} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 hover:opacity-80 transition-opacity group"
                      onClick={(e) => !pkg.tripadvisor_link && e.preventDefault()}
                    >
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#00aa6c">
                        <circle cx="12" cy="12" r="12"/>
                        <circle cx="8" cy="12" r="3" fill="white"/>
                        <circle cx="16" cy="12" r="3" fill="white"/>
                        <circle cx="8" cy="12" r="1.5" fill="#00aa6c"/>
                        <circle cx="16" cy="12" r="1.5" fill="#00aa6c"/>
                      </svg>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div
                            key={i}
                            className={`w-4 h-4 rounded-full ${
                              pkg.tripadvisor_rating >= i 
                                ? 'bg-[#00aa6c]' 
                                : pkg.tripadvisor_rating >= i - 0.5
                                  ? 'bg-gradient-to-r from-[#00aa6c] from-50% to-gray-200 to-50%'
                                  : 'bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="font-semibold text-slate-700">{pkg.tripadvisor_rating}</span>
                      {pkg.tripadvisor_link && (
                        <span className="text-sm text-[#00aa6c] group-hover:underline">Ver en TripAdvisor</span>
                      )}
                    </a>
                  )}

                  <div className="flex items-center gap-2 text-slate-600">
                    <Clock className="w-5 h-5" />
                    {pkg.days} días / {pkg.nights} noches
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Users className="w-5 h-5" />
                    {pkg.guests || 2} huéspedes
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-4">Descripción</h2>
                <p className="text-slate-600 leading-relaxed">
                  {pkg.description || 'Disfruta de una experiencia única en uno de los destinos más codiciados del mundo. Este paquete incluye alojamiento de primera clase, desayuno diario y todas las comodidades que necesitas para unas vacaciones perfectas.'}
                </p>
              </div>

              {/* Inclusions & Exclusions */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-emerald-50 rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Check className="w-5 h-5 text-emerald-600" />
                    Incluido
                  </h3>
                  <ul className="space-y-3">
                    {inclusions.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-slate-700">
                        <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-slate-50 rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <X className="w-5 h-5 text-slate-400" />
                    No Incluido
                  </h3>
                  <ul className="space-y-3">
                    {exclusions.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-slate-600">
                        <X className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Hotel Info */}
              {pkg.hotel_name && (
                <div className="bg-slate-50 rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Building className="w-5 h-5 text-teal-600" />
                    Hotel
                  </h3>
                  <div className="flex items-center gap-4 mb-4">
                    <span className="font-semibold text-slate-900">{pkg.hotel_name}</span>
                    {pkg.hotel_stars && (
                      <div className="flex items-center gap-1">
                        {[...Array(pkg.hotel_stars)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                        ))}
                      </div>
                    )}
                  </div>
                  {pkg.hotel_amenities && (
                    <div className="flex flex-wrap gap-2">
                      {pkg.hotel_amenities.map((amenity, idx) => (
                        <span key={idx} className="px-3 py-1.5 bg-white rounded-full text-sm text-slate-600">
                          {amenity}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Reviews Section */}
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-teal-600" />
                  Opiniones de Viajeros
                </h2>
                
                {/* Rating Summary */}
                <div className="bg-teal-50 rounded-2xl p-6 mb-6">
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="text-5xl font-bold text-teal-600">{rating}</div>
                      <div className="flex items-center justify-center mt-2">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-5 h-5 ${i < Math.floor(rating) ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                        ))}
                      </div>
                      <div className="text-sm text-slate-600 mt-1">{reviewsCount} opiniones</div>
                    </div>
                    <div className="flex-1 pl-6 border-l border-teal-200">
                      <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                        <ShieldCheck className="w-4 h-4 text-teal-600" />
                        <span>Opiniones verificadas</span>
                      </div>
                      <p className="text-slate-700">
                        El 96% de los viajeros recomiendan este paquete
                      </p>
                    </div>
                  </div>
                </div>

                {/* Individual Reviews */}
                <div className="space-y-6">
                  {SAMPLE_REVIEWS.map((review) => (
                    <div key={review.id} className="border-b border-slate-100 pb-6 last:border-0">
                      <div className="flex items-start gap-4">
                        <img
                          src={review.avatar}
                          alt={review.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-slate-900">{review.name}</span>
                            {review.verified && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                                <ShieldCheck className="w-3 h-3" />
                                Verificado
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="flex items-center">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                              ))}
                            </div>
                            <span className="text-sm text-slate-500">{review.date}</span>
                          </div>
                          <p className="text-slate-600">{review.comment}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Conditions */}
              {(pkg.conditions || pkg.presentation_info) && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-amber-600" />
                    Información Importante
                  </h3>
                  {pkg.presentation_info && (
                    <p className="text-slate-700 mb-4">{pkg.presentation_info}</p>
                  )}
                  {pkg.conditions && (
                    <p className="text-slate-600 text-sm">{pkg.conditions}</p>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar - Price Card */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
                {/* Price Header */}
                <div className="bg-gradient-to-r from-teal-500 to-emerald-500 p-6 text-white">
                  <p className="text-sm opacity-80 mb-1">Precio por paquete</p>
                  <div className="flex items-baseline gap-3">
                    <span className="text-4xl font-bold">${pkg.retail_price.toLocaleString()}</span>
                    <span className="text-lg opacity-80">USD</span>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  {/* Price Comparison with ALL competitors */}
                  <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Comparación de Precios</p>
                    
                    {/* Booking */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-[#003580] rounded flex items-center justify-center">
                          <span className="text-white text-xs font-bold">B</span>
                        </div>
                        <span className="text-sm text-slate-600">Booking.com</span>
                      </div>
                      <span className="text-lg text-slate-400 line-through">
                        ${bookingPrice.toLocaleString()}
                      </span>
                    </div>
                    
                    {/* Expedia */}
                    {pkg.comparison_expedia > 0 && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-[#FFCC00] rounded flex items-center justify-center">
                            <span className="text-[#1E3A5F] text-xs font-bold">E</span>
                          </div>
                          <span className="text-sm text-slate-600">Expedia</span>
                        </div>
                        <span className="text-lg text-slate-400 line-through">
                          ${pkg.comparison_expedia.toLocaleString()}
                        </span>
                      </div>
                    )}
                    
                    {/* Despegar */}
                    {pkg.comparison_despegar > 0 && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-[#7B2D8E] rounded flex items-center justify-center">
                            <span className="text-white text-xs font-bold">D</span>
                          </div>
                          <span className="text-sm text-slate-600">Despegar</span>
                        </div>
                        <span className="text-lg text-slate-400 line-through">
                          ${pkg.comparison_despegar.toLocaleString()}
                        </span>
                      </div>
                    )}
                    
                    {/* Bombazoo */}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-gradient-to-r from-teal-500 to-emerald-500 rounded flex items-center justify-center">
                          <Plane className="w-3 h-3 text-white transform -rotate-45" />
                        </div>
                        <span className="text-sm font-semibold text-teal-600">Bombazoo</span>
                      </div>
                      <span className="text-xl font-bold text-teal-600">
                        ${pkg.retail_price.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {/* Savings */}
                  <div className="bg-emerald-100 rounded-xl p-4 text-center">
                    <p className="text-emerald-700 font-semibold">
                      ¡Ahorras ${savings.toLocaleString()} USD!
                    </p>
                    <p className="text-emerald-600 text-sm">
                      ({savingsPercent}% de descuento)
                    </p>
                  </div>

                  {/* Date Type Info */}
                  <div className={`rounded-xl p-4 ${
                    pkg.date_type === 'open' ? 'bg-emerald-50' :
                    pkg.date_type === 'restricted' ? 'bg-amber-50' : 'bg-rose-50'
                  }`}>
                    <p className={`font-semibold text-sm mb-1 ${
                      pkg.date_type === 'open' ? 'text-emerald-700' :
                      pkg.date_type === 'restricted' ? 'text-amber-700' : 'text-rose-700'
                    }`}>
                      {pkg.date_type === 'open' && '🟢 Fechas Abiertas'}
                      {pkg.date_type === 'restricted' && '🟡 Fechas Restringidas'}
                      {pkg.date_type === 'exact' && '🔴 Fechas Exactas (Certificado de Tiempo Compartido)'}
                    </p>
                    <p className={`text-sm ${
                      pkg.date_type === 'open' ? 'text-emerald-600' :
                      pkg.date_type === 'restricted' ? 'text-amber-600' : 'text-rose-600'
                    }`}>
                      {pkg.date_type === 'open' && 'Puedes elegir tus fechas libremente'}
                      {pkg.date_type === 'restricted' && (pkg.date_restrictions || 'Algunas fechas tienen restricciones')}
                      {pkg.date_type === 'exact' && 'Solo disponible en las fechas específicas indicadas'}
                    </p>
                  </div>

                  {/* Available Dates List for exact type */}
                  {pkg.date_type === 'exact' && pkg.available_dates?.length > 0 && (
                    <div className="bg-rose-50 rounded-xl p-4 space-y-3">
                      <p className="font-semibold text-sm text-rose-700">📅 Fechas Disponibles:</p>
                      {pkg.available_dates.map((dateItem, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-3 border border-rose-200">
                          <p className="font-semibold text-slate-900 text-sm">{dateItem.date_range}</p>
                          {dateItem.characteristics && (
                            <p className="text-xs text-slate-600 mt-1">{dateItem.characteristics}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Persons */}
                  <div className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
                    <span className="text-sm text-slate-600">Máximo de personas</span>
                    <span className="font-semibold text-slate-900">{pkg.max_persons || pkg.guests || 2} personas</span>
                  </div>

                  {/* Additional Text */}
                  {pkg.additional_text && (
                    <div className="bg-blue-50 rounded-xl p-4">
                      <p className="text-sm text-blue-700 italic">"{pkg.additional_text}"</p>
                    </div>
                  )}

                  {/* CTA Button */}
                  <Button
                    onClick={() => setShowReservationForm(true)}
                    className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white font-semibold py-4 rounded-xl shadow-lg shadow-teal-500/30 text-lg"
                    data-testid="reservation-button"
                  >
                    📝 Reservar Ahora
                  </Button>
                  
                  <p className="text-xs text-center text-slate-500 mt-2">
                    Completa tus datos y {pkg.stripe_link ? 'procede al pago seguro' : 'un agente te contactará'}
                  </p>

                  {/* Trust Badges */}
                  <div className="pt-4 border-t border-slate-100 space-y-3">
                    {[
                      { icon: ShieldCheck, text: 'Pago 100% seguro' },
                      { icon: Calendar, text: 'Válido por 12 meses' },
                      { icon: Award, text: 'Garantía de mejor precio' }
                    ].map((badge, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                        <badge.icon className="w-4 h-4 text-teal-500" />
                        {badge.text}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Checkout Modal */}
      {showCheckout && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          data-testid="checkout-modal"
        >
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">Completa tu Reserva</h2>
                <button
                  onClick={() => setShowCheckout(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Package Summary */}
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex gap-4">
                  <img
                    src={images[0]}
                    alt={pkg.title}
                    className="w-20 h-20 rounded-lg object-cover"
                  />
                  <div>
                    <h3 className="font-semibold text-slate-900 line-clamp-1">{pkg.title}</h3>
                    <p className="text-sm text-slate-500">{pkg.destination}</p>
                    <p className="text-lg font-bold text-teal-600 mt-1">
                      ${pkg.retail_price.toLocaleString()} USD
                    </p>
                  </div>
                </div>
              </div>

              {/* Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Nombre completo
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Tu nombre"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Correo electrónico
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="tu@email.com"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+52 555 123 4567"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              {/* Total */}
              <div className="bg-emerald-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-600">Certificado</span>
                  <span className="font-semibold">${pkg.retail_price.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-600 flex items-center gap-1">
                    Fee de Reserva
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-200 text-slate-600 text-[10px] cursor-help" title="Fee único por gestión y confirmación de tu reserva">?</span>
                  </span>
                  <span className="font-semibold">$99</span>
                </div>
                <div className="flex items-center justify-between text-emerald-700">
                  <span>Tu ahorro</span>
                  <span className="font-semibold">-${savings.toLocaleString()}</span>
                </div>
                <div className="border-t border-emerald-200 mt-3 pt-3 flex items-center justify-between">
                  <span className="font-bold text-slate-900">Total a Pagar</span>
                  <span className="text-2xl font-bold text-teal-600">${(pkg.retail_price + 99).toLocaleString()}</span>
                </div>
              </div>

              {/* CTA */}
              <Button
                onClick={handleCheckout}
                disabled={submitting}
                className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white font-semibold py-4 rounded-xl text-lg"
              >
                {submitting ? 'Procesando...' : 'Ir a Pagar con Stripe'}
              </Button>

              <p className="text-xs text-center text-slate-500">
                Al continuar, aceptas nuestros términos y condiciones
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Reservation Form Modal */}
      {showReservationForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-teal-600 to-emerald-600 p-5 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">📝 Formulario de Reservación</h2>
                <p className="text-teal-100 text-sm">{pkg.title}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowReservationForm(false)}
                className="text-white hover:bg-white/20"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-5 space-y-4">
              {/* Personal Info */}
              <div className="space-y-3">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Users className="w-4 h-4 text-teal-600" />
                  Información Personal
                </h3>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Nombre *</label>
                    <input
                      type="text"
                      value={reservationData.first_name}
                      onChange={(e) => setReservationData({...reservationData, first_name: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Juan"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Apellido *</label>
                    <input
                      type="text"
                      value={reservationData.last_name}
                      onChange={(e) => setReservationData({...reservationData, last_name: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Pérez"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-slate-600 mb-1">Email *</label>
                  <input
                    type="email"
                    value={reservationData.email}
                    onChange={(e) => setReservationData({...reservationData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="correo@ejemplo.com"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-600 mb-1">Dirección</label>
                  <input
                    type="text"
                    value={reservationData.address}
                    onChange={(e) => setReservationData({...reservationData, address: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Calle, Ciudad, País"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Teléfono 1 *</label>
                    <input
                      type="tel"
                      value={reservationData.phone1}
                      onChange={(e) => setReservationData({...reservationData, phone1: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="+1 234 567 8900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Teléfono 2</label>
                    <input
                      type="tel"
                      value={reservationData.phone2}
                      onChange={(e) => setReservationData({...reservationData, phone2: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="+1 098 765 4321"
                    />
                  </div>
                </div>
              </div>

              {/* Trip Info */}
              <div className="space-y-3 pt-2 border-t border-slate-100">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Plane className="w-4 h-4 text-teal-600" />
                  Información del Viaje
                </h3>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Fecha Probable de Viaje</label>
                    <input
                      type="date"
                      value={reservationData.travel_date}
                      onChange={(e) => setReservationData({...reservationData, travel_date: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Cantidad de Personas</label>
                    <select
                      value={reservationData.num_persons}
                      onChange={(e) => setReservationData({...reservationData, num_persons: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                      {[1,2,3,4,5,6,7,8].map(n => (
                        <option key={n} value={n}>{n} persona{n > 1 ? 's' : ''}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Agent Info */}
              <div className="space-y-3 pt-2 border-t border-slate-100">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-teal-600" />
                  Agente que te Atendió
                </h3>

                <div>
                  <label className="block text-sm text-slate-600 mb-1">Nombre del Agente (si aplica)</label>
                  <input
                    type="text"
                    value={reservationData.agent_name}
                    onChange={(e) => setReservationData({...reservationData, agent_name: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="María García"
                  />
                </div>
              </div>

              {/* Price Summary */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Certificado ({reservationData.num_persons} pers.)</span>
                  <span className="font-medium">${pkg.retail_price.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Fee de Reserva</span>
                  <span className="font-medium">$99</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-slate-200">
                  <span>Total</span>
                  <span className="text-teal-600">${(pkg.retail_price + 99).toLocaleString()}</span>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleReservationSubmit}
                disabled={submitting}
                className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white font-semibold py-4 rounded-xl text-lg"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Procesando...
                  </span>
                ) : (
                  <>
                    {pkg.stripe_link ? '💳 Registrar y Pagar' : '📝 Registrar Reservación'}
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-slate-500">
                Al continuar, aceptas nuestros términos y condiciones
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
